import React from "react";
import { Helmet } from "react-helmet-async";
import { useSettings } from "../lib/settings";

/**
 * Central SEO component. Renders <title>, meta description/keywords, canonical,
 * Open Graph + Twitter Card, hreflang alternates and optional JSON-LD.
 *
 * Every field falls back to sensible site-wide defaults from site_settings.
 */
const SUPPORTED_LANGS = ["en", "es", "ru", "it"];

const buildCanonical = (base, path) => {
  const b = String(base || "").replace(/\/$/, "");
  const p = String(path || "/");
  return `${b}${p.startsWith("/") ? "" : "/"}${p}`;
};

const SEO = ({
  title,
  description,
  keywords,
  image,
  path,
  type = "website",
  robots,
  jsonLd,
  publishedTime,
  modifiedTime,
  author,
}) => {
  const { settings } = useSettings();
  const s = settings || {};
  const base =
    s.seo_canonical_base ||
    (typeof window !== "undefined" ? window.location.origin : "");

  const currentPath =
    path || (typeof window !== "undefined" ? window.location.pathname : "/");

  const finalTitle = title || s.seo_site_title || "Istanbul Free Walking Tours";
  const finalDescription =
    description ||
    s.seo_meta_description ||
    "Small-group Istanbul tours with licensed local guides.";
  const finalKeywords = keywords || s.seo_meta_keywords || "";
  const finalImage = image || s.seo_og_image || "";
  const finalRobots = robots || s.seo_robots || "index, follow";
  const twitter = s.seo_twitter_handle || "";
  const favicon = s.seo_favicon || "";
  const siteName = s.seo_org_name || "Istanbul Free Walking Tours";
  const canonical = buildCanonical(base, currentPath);

  const jsonLdItems = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta name="robots" content={finalRobots} />
      <link rel="canonical" href={canonical} />
      {favicon && <link rel="icon" href={favicon} />}

      {/* hreflang alternates */}
      {SUPPORTED_LANGS.map((lg) => (
        <link
          key={lg}
          rel="alternate"
          hrefLang={lg}
          href={`${canonical}${canonical.includes("?") ? "&" : "?"}lang=${lg}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      {finalImage && <meta property="og:image" content={finalImage} />}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter */}
      <meta name="twitter:card" content={finalImage ? "summary_large_image" : "summary"} />
      {twitter && <meta name="twitter:site" content={twitter} />}
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      {finalImage && <meta name="twitter:image" content={finalImage} />}

      {/* JSON-LD */}
      {jsonLdItems.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
export { buildCanonical };
